import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Heading, HStack, Text } from '@chakra-ui/react';
import html2canvas from 'html2canvas';
import { CopyChartButton, ChartWatermark } from '../../components/ChartSharing';
import { useColorModeValue } from '../../components/ui/color-mode';
import { getEarningsReport } from '../../api';
import { formatEarningsValue } from './earningsFormatting';

function DocumentLink({ label, url }) {
  return url ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>{label} ↗</a>
    : <Text color="fg.muted">{label}: unavailable or not yet filed</Text>;
}

// Comparisons are ordered prior-year, previous, current. One bar is not a comparison.
export function hasQuarterComparison(comparisons) {
  return Array.isArray(comparisons)
    && Number.isFinite(comparisons[2]?.value)
    && comparisons.slice(0, 2).some(period => Number.isFinite(period?.value));
}

export function MetricComparison({ row, ticker }) {
  const chartRef = useRef(null);
  const [copyStatus, setCopyStatus] = useState('');
  useEffect(() => {
    if (copyStatus !== 'copied' && copyStatus !== 'error') return undefined;
    const timer = setTimeout(() => setCopyStatus(''), 2000);
    return () => clearTimeout(timer);
  }, [copyStatus]);
  const background = useColorModeValue('#ffffff', '#111827');
  const foreground = useColorModeValue('#1a202c', '#e5e7eb');
  async function copyImage() {
    if (copyStatus === 'copying') return;
    if (!navigator.clipboard?.write || !window.ClipboardItem) {
      setCopyStatus('error');
      return;
    }
    setCopyStatus('copying');
    try {
      const image = html2canvas(chartRef.current, { backgroundColor: background, scale: 2 })
        .then(canvas => new Promise((resolve, reject) => canvas.toBlob(
          blob => blob ? resolve(blob) : reject(new Error('Image conversion failed')), 'image/png'
        )));
      // Start the clipboard write during the click gesture, including on Safari.
      image.catch(() => {});
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': image })]);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('error');
    }
  }

  const values = row.comparisons.map(p => p.value).filter(v => v != null);
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const range = max - min || 1;
  const y = value => 145 - (value - min) / range * 125;
  const baseline = y(0);
  return <Box ref={chartRef} position="relative" mt={3} p={4} pb={8} borderWidth="1px" borderRadius="lg" style={{ backgroundColor: background, color: foreground }}>
    <HStack justify="space-between" align="start" gap={3} mb={2}>
      <Box minW={0}>
        {ticker && <Text fontSize="sm" mb={1}>{ticker.toUpperCase()}</Text>}
        <Heading fontSize="20px" lineHeight="1.35">{row.label} — quarterly comparison</Heading>
      </Box>
      <CopyChartButton status={copyStatus} onClick={copyImage} />
    </HStack>
    <Text fontSize="sm">{row.unit === 'USD/shares' ? 'USD per share' : row.unit}. {row.comparisons.some(p => p.basis === 'At quarter end') ? 'Balances at quarter end.' : 'Standalone quarters; cumulative values are not plotted.'}</Text>
    <svg viewBox="0 0 660 235" role="img" aria-label={`${row.label}: prior-year quarter, previous quarter, current quarter`} style={{ width: '100%', maxWidth: 800 }}>
      <line x1="25" x2="635" y1={baseline} y2={baseline} stroke="currentColor" opacity="0.4" />
      {row.comparisons.map((period, i) => {
        const x = 110 + i * 220;
        const valueY = period.value == null ? baseline : y(period.value);
        return <g key={i}>
          <title>{period.label}: {period.basis}</title>
          {period.value != null && <rect x={x - 42} width="84" y={Math.min(valueY, baseline)} height={Math.max(Math.abs(baseline - valueY), 1)} fill={['#718096', '#319795', '#3182ce'][i]} />}
          <text x={x} y="166" textAnchor="middle" fill="currentColor" fontSize="13">{formatEarningsValue(period.value, 'abbreviated', row.unit)}</text>
          <text x={x} y="188" textAnchor="middle" fill="currentColor" fontSize="12">{period.label}</text>
          <text x={x} y="209" textAnchor="middle" fill="currentColor" fontSize="12">{period.end || 'Period unavailable'}</text>
        </g>;
      })}
    </svg>
    <ChartWatermark />
  </Box>;
}

export default function StockEarningsReport({ embedded = false }) {
  const { ticker } = useParams();
  const [requestState, setRequestState] = useState({ loading: true });
  const [retry, setRetry] = useState(0);
  const [format, setFormat] = useState('full');
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setRequestState({ loading: true });
    setSelected(null);
    setMessage('');
    getEarningsReport({ ticker, signal: controller.signal }).then(data => {
      if (!controller.signal.aborted) setRequestState({ data });
    }).catch(error => {
      if (!controller.signal.aborted) setRequestState({ error: error.message });
    });
    return () => controller.abort();
  }, [ticker, retry]);
  async function copy(value, unit) {
    const text = formatEarningsValue(value, format, unit);
    try {
      await navigator.clipboard.writeText(text);
      setMessage(`Copied ${text}`);
    } catch {
      setMessage(`Clipboard unavailable. Copy this value manually: ${text}`);
    }
  }
  useEffect(() => {
    if (selected) document.getElementById('earnings-comparison')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [selected]);
  const { data, loading, error } = requestState;
  return <Box p={embedded ? 0 : { base: 3, md: 6 }} maxW="1400px" mx="auto">
    {!embedded && <Text color="fg.muted" fontSize="sm">{ticker?.toUpperCase()} / Earnings reports</Text>}
    <Heading mt={2} mb={4}>Most recent earnings</Heading>
    {loading && <Text role="status">Loading earnings report…</Text>}
    {error && <Box role="alert"><Text>{error}</Text><Button mt={3} onClick={() => setRetry(v => v + 1)}>Retry</Button></Box>}
    {data?.status === 'unavailable' && <Text>{data.message}</Text>}
    {data?.pending_release && <Box borderWidth="1px" borderColor="blue.400" borderRadius="lg" p={4} mb={6}>
      <Heading fontSize="22px" lineHeight="1.35">New earnings release: period ended {data.pending_release.period_end}</Heading>
      <Text my={2}>The corresponding 10-Q or year-end 10-K is not yet available. Statements and quarterly comparisons will appear after the filing is available. The older filed statements below are labeled separately.</Text>
      <HStack gap={5} flexWrap="wrap"><DocumentLink label="Earnings press release" url={data.pending_release.press_release} /><DocumentLink label="Earnings presentation" url={data.pending_release.presentation} /><DocumentLink label="Associated 8-K" url={data.pending_release.source_8k} /></HStack>
    </Box>}
    {data?.period_end && <>
      <Heading fontSize="26px" lineHeight="1.35">{data.pending_release ? 'Latest filed statements: ' : ''}{data.period_label}</Heading>
      <Text my={2}>Period ended {data.period_end} · Filed {data.filed}</Text>
      <HStack gap={5} flexWrap="wrap" my={4}>
        <DocumentLink label="Earnings press release" url={data.documents.press_release} />
        <DocumentLink label="Earnings presentation" url={data.documents.presentation} />
        <DocumentLink label={`Complete ${data.documents.filing_type}`} url={data.documents.filing} />
        {data.documents.source_8k && <DocumentLink label="Associated 8-K" url={data.documents.source_8k} />}
      </HStack>
      {data.documents.filing_type === '10-K' && <Text fontSize="sm" mb={3}>Year-end report: the 10-K replaces the 10-Q. Tables retain annual periods; graphs show Q4 where a standalone quarter can be reported or derived.</Text>}
      {data.warnings?.map((warning, i) => <Text key={i} role="status" mb={2}>{warning}</Text>)}
      <Box borderWidth="1px" borderRadius="lg" p={4} my={5}>
        <label htmlFor="earnings-copy-format">Copy number format </label>
        <select id="earnings-copy-format" value={format} onChange={e => setFormat(e.target.value)} style={{ padding: 8, color: 'inherit', background: 'transparent', border: '1px solid #718096', borderRadius: 6 }}>
          <option value="full">Full number — $10,000,000</option>
          <option value="abbreviated">Abbreviated — $10M</option>
          <option value="words">With words — $10 million</option>
        </select>
        <Text fontSize="sm" mt={2}>Click a number to copy it. Copied values expand the statement’s thousands or millions into the selected format. Tables retain the filing’s original periods and scale.</Text>
        <Text role="status" mt={2}>{message}</Text>
      </Box>
      {data.statements.map((statement, s) => <Box key={s} mb={8}>
        <Heading fontSize="22px" lineHeight="1.35" mb={2}>{{ income: 'Income statement', balance_sheet: 'Balance sheet', cash_flow: 'Cash flow statement' }[statement.kind] || statement.title}</Heading>
        <DocumentLink label="Source statement" url={statement.source_url} />
        <Box overflowX="auto" borderWidth="1px" borderRadius="lg" mt={3}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <caption style={{ textAlign: 'left', padding: 12 }}>As filed. Use “Compare quarters” for comparable quarterly values.</caption>
            <tbody>{statement.rows.map((row, r) => <tr key={r} style={{ borderBottom: '1px solid #71809640' }}>
              {row.cells.map((cell, c) => {
                const Tag = cell.header ? 'th' : 'td';
                return <Tag key={c} colSpan={cell.colspan} rowSpan={cell.rowspan} style={{ padding: '10px 12px', textAlign: c ? 'right' : 'left', minWidth: c ? 100 : 240 }}>
                  {cell.value != null ? <button type="button" title={`Copy ${formatEarningsValue(cell.value, format, row.unit)}`} aria-label={`Copy ${row.label}: ${formatEarningsValue(cell.value, format, row.unit)}`} onClick={() => copy(cell.value, row.unit)} style={{ cursor: 'copy', font: 'inherit', color: 'inherit', background: 'transparent', border: 0, textDecoration: 'underline', textDecorationStyle: 'dotted' }}>{cell.text}</button> : cell.text}
                  {c === 0 && hasQuarterComparison(row.comparisons) && <Button display="block" size="xs" variant="ghost" mt={1} onClick={() => setSelected({ statement: s, row: r })}>Compare quarters</Button>}
                </Tag>;
              })}
            </tr>)}</tbody>
          </table>
        </Box>
        {selected?.statement === s && hasQuarterComparison(statement.rows[selected.row]?.comparisons) && <Box id="earnings-comparison"><MetricComparison key={`${s}-${selected.row}`} row={statement.rows[selected.row]} ticker={ticker} /></Box>}
      </Box>)}
    </>}
  </Box>;
}
